import { useState, useEffect, FormEvent } from 'react';
import {
  getSupportStaff,
  createSupportStaff,
  updateSupportStaff,
  deleteSupportStaff,
  SupportStaffMember,
} from '../api/client';
import ImageUploader from '../components/ImageUploader';
import { useSportConfig } from '../hooks/useSportConfig';

/**
 * Club Staff tab, a role-lens over Members (role=supportStaff), club-wide.
 *
 * Staff ARE Members, so the same person also shows on the Members tab (base
 * identity edits sync there). This page is the rich editor: it adds the
 * staff role, photo, and bio shown on the public Club Staff page.
 * Club + Pro only (the route + sidebar link are plan-gated).
 */

interface FormState {
  name: string;
  email: string;
  phone: string;
  image: string;
  staffRole: string;
  bio: string;
  showEmailPublicly: boolean;
}

function emptyForm(): FormState {
  return { name: '', email: '', phone: '', image: '', staffRole: '', bio: '', showEmailPublicly: false };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts[1]?.[0] || '')).toUpperCase();
}

export default function SupportStaff() {
  const sportConfig = useSportConfig();
  const [staff, setStaff] = useState<SupportStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SupportStaffMember | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      setStaff(await getSupportStaff());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load club staff');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  }

  function openEdit(s: SupportStaffMember) {
    setEditing(s);
    setForm({
      name: s.name,
      email: s.email,
      phone: s.phone,
      image: s.image,
      staffRole: s.staffRole || '',
      bio: s.bio || '',
      showEmailPublicly: !!s.showEmailPublicly,
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (uploadingPhoto) { setError('Wait for the photo upload to finish before saving'); return; }
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.staffRole) { setError('Pick a role'); return; }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        image: form.image,
        staffRole: form.staffRole,
        bio: form.bio.trim(),
        showEmailPublicly: form.showEmailPublicly,
      };
      if (editing) {
        await updateSupportStaff(editing.memberId, payload);
        setSuccess(`Updated ${form.name.trim()}`);
      } else {
        await createSupportStaff(payload);
        setSuccess(`Added ${form.name.trim()}`);
      }
      await load();
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save staff member');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(s: SupportStaffMember) {
    if (!confirm(`Remove ${s.name} from Club Staff? If they're also a parent/coach, that stays, only the staff role is removed.`)) return;
    try {
      await deleteSupportStaff(s.memberId);
      setSuccess(`${s.name} removed from Club Staff`);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove staff member');
    }
  }

  return (
    <div className="tm-page-shell">
      <div className="page-head">
        <div>
          <h1>Club Staff</h1>
          <p className="subtitle">
            Your club's support &amp; committee staff, physios, media, secretary,
            chairman and more. They appear on your public Club Staff page (Club &amp; Pro).
          </p>
        </div>
        <div className="head-actions">
          <button className="ebtn ebtn-primary" onClick={openCreate}>+ Add staff</button>
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      {loading ? (
        <p className="loading">Loading club staff…</p>
      ) : staff.length === 0 ? (
        <div className="empty-state">
          <p>No club staff yet.</p>
          <button className="ebtn ebtn-primary" style={{ marginTop: 12 }} onClick={openCreate}>+ Add your first staff member</button>
        </div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.memberId}>
                  <td>
                    <div className="tm-person">
                      {s.image ? (
                        <img className="tm-avatar" src={s.image} alt="" />
                      ) : (
                        <span className="tm-avatar tm-avatar-empty">{initials(s.name)}</span>
                      )}
                      <span className="tm-person-name">{s.name}</span>
                    </div>
                  </td>
                  <td>{s.staffRole ? <span className="tm-chip">{s.staffRole}</span> : <span className="tm-muted">-</span>}</td>
                  <td>{s.email ? <a className="tm-link" href={`mailto:${s.email}`}>{s.email}</a> : <span className="tm-muted">-</span>}</td>
                  <td>
                    <div className="tm-actions">
                      <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(s)}>Edit</button>
                      <button className="ebtn ebtn-danger ebtn-sm" onClick={() => handleRemove(s)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="tm-modal-overlay" onClick={closeModal}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>{editing ? 'Edit staff member' : 'Add staff member'}</h2>
              <button type="button" className="tm-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="tm-modal-body">
                <label className="tm-field">
                  <span>Name *</span>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </label>
                <label className="tm-field">
                  <span>Role *</span>
                  <select value={form.staffRole} onChange={e => setForm({ ...form, staffRole: e.target.value })} required>
                    <option value="">Select a role…</option>
                    {sportConfig.staffRoles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>Email</span>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </label>
                  <label className="tm-field">
                    <span>Phone</span>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </label>
                </div>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 4px', fontSize: '0.9rem',
                    cursor: form.email.trim() ? 'pointer' : 'not-allowed', opacity: form.email.trim() ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.showEmailPublicly}
                    disabled={!form.email.trim()}
                    onChange={e => setForm({ ...form, showEmailPublicly: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Show this email on the public club staff page
                </label>
                <label className="tm-field">
                  <span>Bio <span className="tm-optional">(optional)</span></span>
                  <textarea
                    value={form.bio}
                    maxLength={180}
                    rows={3}
                    placeholder="A short blurb, a sentence or two."
                    onChange={e => setForm({ ...form, bio: e.target.value })}
                  />
                  <small className="tm-muted">
                    {form.bio.length}/180 · shown on the public Club Staff page, the photo flips to reveal it. Leave blank and the card won't flip.
                  </small>
                </label>
                <div className="tm-field">
                  <span>Photo (shown on the public Club Staff page)</span>
                  <ImageUploader
                    currentImage={form.image || undefined}
                    onUpload={url => setForm(f => ({ ...f, image: url }))}
                    onUploadingChange={setUploadingPhoto}
                    label="Upload photo"
                  />
                </div>
              </div>
              <div className="tm-modal-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={saving || uploadingPhoto}>
                  {uploadingPhoto ? 'Uploading photo…' : saving ? 'Saving…' : editing ? 'Save changes' : 'Add staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
